/* eslint-disable security/detect-object-injection */
// Shared types + bucket → action mapping for the Batch Administrator page.
// The backend already groups 40+ dataset statuses into 8 buckets (see
// cli/pkg/insight/api_batch_submit.go :: bucketToStatuses). Here we add
// the per-bucket *action* semantics: which status within a bucket is
// actually eligible for a clicky-click endpoint, and which status means
// "send this row to Review Datasets Bulk for human review."

export interface StatusResponse {
  prefix: string;
  total: number;
  buckets: Record<string, number>;
  raw: Record<string, number>;
  hint: Record<string, string>;
}

export type BucketAction = 'submit' | 'mark-clean' | 'advance' | null;

export interface BucketInfo {
  index: number;
  key: string;
  label: string;
  shortLabel: string;
  total: number;
  action: BucketAction;
  // Datasets at the status our endpoint actually targets (not the bucket total).
  // e.g., bucket 3 total can include samples_packing which is not eligible for
  // mark-clean; only samples_scanned is.
  eligibleCount: number;
  // Datasets at *_phi_detected within this bucket. These get routed to the
  // Review Datasets Bulk page for human review (safety layer per clicky-
  // click-problem.md §181).
  phiDetectedCount: number;
  // Pipeline-driven statuses within this bucket (no manual action, just waiting).
  inProgressCount: number;
}

export const BUCKET_ORDER = [
  '0_canceled_or_recalled',
  '1_created_at_rest',
  '2_metadata_fetching',
  '3_samples_processing_or_review',
  '4_samples_ready_place_order_next',
  '5_full_dataset_processing',
  '6_full_scan_done_review_next',
  '7_full_delivering',
  '8_delivered',
] as const;

export const BUCKET_LABELS: Record<string, {full: string; short: string}> = {
  '0_canceled_or_recalled': {full: 'Canceled / recalled', short: 'Canceled'},
  '1_created_at_rest': {full: 'At rest', short: 'At rest'},
  '2_metadata_fetching': {full: 'Fetching metadata', short: 'Metadata'},
  '3_samples_processing_or_review': {
    full: 'Samples processing / review',
    short: 'Samples',
  },
  '4_samples_ready_place_order_next': {
    full: 'Samples ready — advance next',
    short: 'Ready',
  },
  '5_full_dataset_processing': {
    full: 'Full pipeline processing',
    short: 'Full',
  },
  '6_full_scan_done_review_next': {
    full: 'Scan done — review next',
    short: 'Scan done',
  },
  '7_full_delivering': {full: 'Delivering', short: 'Delivering'},
  '8_delivered': {full: 'Delivered', short: 'Delivered'},
};

// Which raw status inside each bucket is eligible for our batch action.
// Single source of truth — mirrors the backend handler's `Statuses:` filters.
//
// Note: bucket 3 (samples_scanned) intentionally has NO eligible status here.
// Per Darcy 2026-04-30 (Batch Admin Bugs Part 2), samples_scanned datasets
// must go through the Review page — flipping them in batch from Batch Admin
// would skip per-study OCR-PHI review (PHI exposure path).
export const BUCKET_ELIGIBLE_STATUSES: Record<string, string[]> = {
  '1_created_at_rest': ['dataset_created'],
  '4_samples_ready_place_order_next': ['metadata_email_sent'],
  '6_full_scan_done_review_next': ['dataset_scanned'],
};

// Which raw status means "OCR thinks there might be PHI — send to human review."
export const BUCKET_PHI_DETECTED_STATUSES: Record<string, string[]> = {
  '3_samples_processing_or_review': ['samples_phi_detected'],
  '6_full_scan_done_review_next': ['dataset_phi_detected'],
};

export const BUCKET_ACTION: Record<string, BucketAction> = {
  '0_canceled_or_recalled': null,
  '1_created_at_rest': 'submit',
  '2_metadata_fetching': null,
  // bucket 3 (samples_scanned) has NO action from Batch Admin — datasets
  // here must go through the Review page so per-study OCR-PHI review fires.
  // See bucket-model.ts BUCKET_ELIGIBLE_STATUSES note + api_batch_mark_clean.go.
  '3_samples_processing_or_review': null,
  '4_samples_ready_place_order_next': 'advance',
  '5_full_dataset_processing': null,
  '6_full_scan_done_review_next': 'mark-clean',
  '7_full_delivering': null,
  '8_delivered': null,
};

export function computeBuckets(status: StatusResponse): BucketInfo[] {
  return BUCKET_ORDER.map((key, index) => {
    const total = status.buckets[key] ?? 0;
    const eligibleStatuses = BUCKET_ELIGIBLE_STATUSES[key] ?? [];
    const phiStatuses = BUCKET_PHI_DETECTED_STATUSES[key] ?? [];

    const eligibleCount = eligibleStatuses.reduce(
      (sum, s) => sum + (status.raw[s] ?? 0),
      0
    );
    const phiDetectedCount = phiStatuses.reduce(
      (sum, s) => sum + (status.raw[s] ?? 0),
      0
    );
    const inProgressCount = Math.max(
      0,
      total - eligibleCount - phiDetectedCount
    );

    return {
      index,
      key,
      label: BUCKET_LABELS[key]?.full ?? key,
      shortLabel: BUCKET_LABELS[key]?.short ?? key,
      total,
      action: BUCKET_ACTION[key] ?? null,
      eligibleCount,
      phiDetectedCount,
      inProgressCount,
    };
  });
}

export function prefixHasActionAvailable(status: StatusResponse): boolean {
  return computeBuckets(status).some(
    b => b.action !== null && b.eligibleCount > 0
  );
}

// "Jump to next action" — scan alphabetical prefixes, lowest bucket index first.
export function findNextAction(
  prefixStatuses: Record<string, StatusResponse | undefined>
): {prefix: string; bucket: number} | null {
  const sortedPrefixes = Object.keys(prefixStatuses).sort();
  for (const prefix of sortedPrefixes) {
    const status = prefixStatuses[prefix];
    if (!status) continue;
    const buckets = computeBuckets(status);
    const actionable = buckets.find(
      b => b.action !== null && b.eligibleCount > 0
    );
    if (actionable) {
      return {prefix, bucket: actionable.index};
    }
  }
  return null;
}

// Visual state drives the bucket pill color. Deliberate order of precedence.
export type BucketVisualState =
  | 'empty'
  | 'waiting'
  | 'action'
  | 'partial-review'
  | 'terminal-success'
  | 'terminal-canceled';

export function bucketVisualState(bucket: BucketInfo): BucketVisualState {
  if (bucket.total === 0) return 'empty';
  if (bucket.key === '0_canceled_or_recalled') return 'terminal-canceled';
  if (bucket.key === '8_delivered') return 'terminal-success';
  if (bucket.action !== null && bucket.eligibleCount > 0) {
    return bucket.phiDetectedCount > 0 ? 'partial-review' : 'action';
  }
  return 'waiting';
}
